
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";

interface ProducerSelectProps {
  value: string;
  onChange: (value: string) => void;
  onProducerNameChange?: (name: string) => void;
}

interface ProducerOption {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
}

export const ProducerSelect = ({ 
  value, 
  onChange,
  onProducerNameChange 
}: ProducerSelectProps) => {
  const [initialLoad, setInitialLoad] = useState(true);

  const { data: producers = [], isLoading } = useQuery({
    queryKey: ['producers'],
    queryFn: async () => {
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'producer');

      if (roleError) {
        console.error("Error fetching producer roles:", roleError);
        throw roleError;
      }

      if (!roleData?.length) {
        console.log("No producers found in user_roles");
        return [];
      }

      const producerIds = roleData.map(r => r.user_id);
      
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', producerIds);

      if (profileError) {
        console.error("Error fetching producer profiles:", profileError);
        throw profileError;
      }

      const mappedProducers = (profiles || []).map((producer): ProducerOption => ({
        value: producer.id,
        label: producer.first_name && producer.last_name ? 
          `${producer.first_name} ${producer.last_name}` : 
          'Unknown Producer',
        firstName: producer.first_name || '',
        lastName: producer.last_name || ''
      }));

      console.log("Producers loaded:", mappedProducers);
      
      return mappedProducers;
    },
    initialData: [],
  });

  // When producers are loaded, set the producer name for the current value if it exists
  useEffect(() => {
    if (initialLoad && producers.length > 0 && value) {
      const selectedProducer = producers.find(p => p.value === value);
      if (selectedProducer && onProducerNameChange) {
        const fullName = `${selectedProducer.firstName} ${selectedProducer.lastName}`.trim();
        console.log("Setting initial producer name:", fullName);
        onProducerNameChange(fullName);
      }
      setInitialLoad(false);
    }
  }, [producers, value, initialLoad, onProducerNameChange]);

  // Debug and update producer name when selection changes
  useEffect(() => {
    if (value) {
      console.log("Current producer ID:", value);
      const selectedProducer = producers.find(p => p.value === value);
      console.log("Selected producer:", selectedProducer);
      
      // If onProducerNameChange callback is provided, call it with the full name
      if (selectedProducer && onProducerNameChange) {
        const fullName = `${selectedProducer.firstName} ${selectedProducer.lastName}`.trim();
        console.log("Updating producer name to:", fullName);
        onProducerNameChange(fullName);
      }
    }
  }, [value, producers, onProducerNameChange]);

  const handleProducerChange = (newValue: string) => {
    console.log("Producer selected:", newValue);
    onChange(newValue);
    
    // If the selection is cleared, also clear the producer name
    if (!newValue && onProducerNameChange) {
      onProducerNameChange('');
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-gray-900">Producer</Label>
      <Select
        value={value}
        onValueChange={handleProducerChange}
        disabled={isLoading}
      >
        <SelectTrigger className="w-full h-14">
          <SelectValue placeholder="Select producer">
            {value ? producers.find(p => p.value === value)?.label || "Select producer" : "Select producer"}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {producers.length === 0 ? (
            <SelectItem value="no-producer" disabled>
              No producers found
            </SelectItem>
          ) : (
            producers.map((producer) => (
              <SelectItem key={producer.value} value={producer.value}>
                {producer.label}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>
  );
};
